import * as React from 'react';
import { countries } from 'platform-data';
import { Flex, Alert, Button, InputProps, TextareaProps } from '@chakra-ui/react';

import { Input } from '../Input';
import { Textarea } from '../Textarea';
import { AutocompleteInput } from '../AutocompleteInput';
import { withConfirm } from '../withConfirm';
import { CustomFormControl, DirectionProp } from '../CustomFormControl';

interface IProps extends DirectionProp {
  id?: string;
  addressValue?: string;
  cityValue?: string;
  postcodeValue?: string;
  countryValue?: string;
  addressLabel?: string;
  addressSubText?: string;
  cityLabel?: string;
  postcodeLabel?: string;
  countryLabel?: string;
  inputHelperText?: string;
  addressErrorText?: string;
  cityErrorText?: string;
  countryErrorText?: string;
  cleanSuggestionsAfterSave?: boolean;
  hasSaveButton?: boolean;
  complete?: boolean;
  forwardedRef?: ((instance: HTMLInputElement | null) => void) | React.RefObject<HTMLInputElement> | null | undefined;
  onChangeAddress?: (event: React.FormEvent<HTMLTextAreaElement>) => void;
  onChangeCity?: (event: React.FormEvent<HTMLInputElement>) => void;
  onChangePostcode?: (event: React.FormEvent<HTMLInputElement>) => void;
  onChangeCountry?: (value: string) => void;
  onBlur?: (evt: any) => void;
  onFocus?: (evt: any) => void;
  onSaveAndClose: (address: any) => void;
  textareaProps?: TextareaProps;
  inputProps?: InputProps;
}

interface IState {
  countryValue: string;
  countrySuggestions: string[];
}

class AddressInput extends React.Component<IProps, IState> {
  countryNames = countries.map(country => country.countryName);

  constructor(props: IProps & IState) {
    super(props);
    this.state = {
      countryValue: props.countryValue ? props.countryValue : '',
      countrySuggestions: this.countryNames,
    };
  }

  refreshSuggestions = () => {
    this.setState({
      countryValue: this.props.countryValue ? this.props.countryValue : '',
      countrySuggestions: this.countryNames,
    });
  };

  handleCountryChange = (evt: any) => {
    const value = evt.currentTarget.value || '';
    const suggestions = this.countryNames.filter(s => s.toLowerCase().includes(value.toLowerCase()));

    this.setState({ countrySuggestions: suggestions, countryValue: value });
    if (this.countryNames.includes(value) && this.props.onChangeCountry) {
      this.props.onChangeCountry(value);
    }
  };

  handleOnSaveAndCloseClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    this.saveAndClose();
  };

  saveAndClose = () => {
    this.props.onSaveAndClose({});
  };

  hasError = this.props.addressErrorText || this.props.cityErrorText || this.props.countryErrorText;

  render() {
    return (
      <CustomFormControl
        id={this.props.id}
        controlProps={{
          isInvalid: !!this.hasError,
        }}
        direction={this.props.direction}
        error={`${this.props.addressErrorText} ${this.props.cityErrorText} ${this.props.countryErrorText}`.trim()}
        forwardedRef={this.props.forwardedRef}
      >
        <Textarea
          id={`${this.props.id}-address-lines`}
          label={this.props.addressLabel}
          subText={this.props.addressSubText}
          onChange={this.props.onChangeAddress}
          value={this.props.addressValue}
          showErrorText={!!this.props.addressErrorText}
          textareaProps={this.props.textareaProps}
        />

        <Flex
          flexDirection={{ base: 'column', md: 'row' }}
          columnGap={3}
          rowGap={6}
          my={6}
          data-selector="city-postcode-wrapper"
        >
          <Input
            id={`${this.props.id}-city`}
            label={this.props.cityLabel}
            onChange={this.props.onChangeCity}
            value={this.props.cityValue}
            showErrorText={!!this.props.cityErrorText}
            inputProps={this.props.inputProps}
          />

          <Input
            id={`${this.props.id}-postcode`}
            label={this.props.postcodeLabel}
            onChange={this.props.onChangePostcode}
            value={this.props.postcodeValue}
            inputProps={this.props.inputProps}
          />
        </Flex>

        <AutocompleteInput
          id={`${this.props.id}-country`}
          label={this.props.countryLabel}
          onChange={this.handleCountryChange}
          value={this.state.countryValue}
          showErrorText={!!this.props.countryErrorText}
          suggestions={this.state.countrySuggestions}
          onBlur={(_event: React.FormEvent<any>) => {
            this.setState({
              countryValue: this.props.countryValue!,
              countrySuggestions: this.countryNames,
            });
          }}
        />

        {this.props.addressErrorText || this.props.cityErrorText || this.props.countryErrorText ? (
          <Alert status="error" data-selector="error-text" mt={3}>
            {`${this.props.addressErrorText} ${this.props.cityErrorText} ${this.props.countryErrorText}`.trim()}
          </Alert>
        ) : null}

        {this.props.hasSaveButton && (
          <Flex justifyContent="end" mt={6}>
            <Button
              id={`add-value-modal-confirm-button`}
              data-selector={`add-value-modal-confirm-button`}
              disabled={!this.props.complete}
              onClick={event => {
                if (this.props.cleanSuggestionsAfterSave) {
                  this.refreshSuggestions();
                }
                this.handleOnSaveAndCloseClick(event);
              }}
              variant="solid"
              size="lg"
            >
              Save &amp; Continue
            </Button>
          </Flex>
        )}
      </CustomFormControl>
    );
  }
}

const AddressInputWithConfirm = withConfirm(AddressInput);

export { AddressInput, AddressInputWithConfirm };
